declare global {
  interface Window {
    cv: any;
  }
}

export interface OpenCVDetectionResult {
  isAligned: boolean;
  debug: {
    contourArea: number;
    frameArea: number;
    contourAreaRatio: number;
    aspectRatio?: number;
    vertices?: number;
  };
}

export const loadOpenCV = async (): Promise<void> => {
  return await new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Not running in browser'));
      return;
    }

    if ((window as any).cv) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://docs.opencv.org/4.5.5/opencv.js';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      const checkReady = setInterval(() => {
        if ((window as any).cv && (window as any).cv.Mat) {
          clearInterval(checkReady);
          resolve();
        }
      }, 100);

      // Optional timeout
      setTimeout(() => {
        clearInterval(checkReady);
        if (!(window as any).cv || !(window as any).cv.Mat) {
          reject(new Error('Timeout: OpenCV not ready'));
        }
      }, 5000);
    };

    script.onerror = () => {
      reject(new Error('Failed to load OpenCV script'));
    };

    document.body.appendChild(script);
  });
};

export const detectAlignmentWithOpenCV = (
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  guidingFrame?: { x: number; y: number; width: number; height: number },
): OpenCVDetectionResult => {
  const result: OpenCVDetectionResult = {
    isAligned: false,
    debug: {
      contourArea: 0,
      frameArea: 0,
      contourAreaRatio: 0,
    },
  };

  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  ctx.drawImage(video, 0, 0);

  const cleanupMats: any[] = [];
  let contours;
  let hierarchy;

  try {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const src = window.cv.matFromImageData(imageData);
    const gray = new window.cv.Mat();
    const blur = new window.cv.Mat();
    const edges = new window.cv.Mat();
    contours = new window.cv.MatVector();
    hierarchy = new window.cv.Mat();

    cleanupMats.push(src, gray, blur, edges);

    window.cv.cvtColor(src, gray, window.cv.COLOR_RGBA2GRAY);
    window.cv.GaussianBlur(gray, blur, new window.cv.Size(5, 5), 0);
    window.cv.Canny(blur, edges, 1, 10);

    window.cv.findContours(
      edges,
      contours,
      hierarchy,
      window.cv.RETR_EXTERNAL,
      window.cv.CHAIN_APPROX_SIMPLE,
    );

    let largestContour: any = null;
    let maxArea = 0;

    for (let i = 0; i < contours.size(); i++) {
      const contour = contours.get(i);
      const area = window.cv.contourArea(contour);
      if (area > maxArea) {
        maxArea = area;
        largestContour = contour;
      } else {
        contour.delete();
      }
    }

    const frameArea = canvas.width * canvas.height;
    const contourAreaRatio = maxArea / frameArea;

    result.debug = {
      ...result.debug,
      contourArea: maxArea,
      frameArea,
      contourAreaRatio,
    };

    if (largestContour && maxArea > 5000) {
      const approx = new window.cv.Mat();
      const peri = window.cv.arcLength(largestContour, true);
      window.cv.approxPolyDP(largestContour, approx, 0.02 * peri, true);
      result.debug.vertices = approx.rows;

      if (approx.rows >= 4 && approx.rows <= 8) {
        const rect = window.cv.boundingRect(largestContour);
        const aspectRatio = rect.width / rect.height;
        result.debug.aspectRatio = aspectRatio;

        let aligned = true;
        if (guidingFrame) {
          const marginW = guidingFrame.width * 0.1;
          const marginH = guidingFrame.height * 0.1;

          aligned =
            rect.x >= guidingFrame.x - marginW &&
            rect.y >= guidingFrame.y - marginH &&
            rect.x + rect.width <=
              guidingFrame.x + guidingFrame.width + marginW &&
            rect.y + rect.height <=
              guidingFrame.y + guidingFrame.height + marginH;
        }

        if (
          aligned &&
          aspectRatio > 0.3 &&
          aspectRatio < 5.0 &&
          contourAreaRatio > 0.002 &&
          contourAreaRatio < 0.999 &&
          rect.width > 5 &&
          rect.height > 3
        ) {
          console.log('✅ Check aligned!');
          result.isAligned = true;
        }
      }

      approx.delete();
      largestContour.delete();
    }
  } catch (error) {
    console.error('OpenCV processing error:', error);
  } finally {
    for (const mat of cleanupMats) {
      try {
        mat.delete();
      } catch (e) {
        console.warn('Cleanup error:', e);
      }
    }
    hierarchy?.delete?.();
    contours?.delete?.();
  }

  return result;
};
